import React, { useState } from 'react';
import { useEntity } from '../../contexts/EntityContext';
import { 
  createSecureCredentials, 
  createDataIngestionWorkflow, 
  testConnection, 
  DATA_SOURCES 
} from '../../services/n8nService';
import './DataSourceSetup.css';

function DataSourceSetup() {
  const { currentEntity } = useEntity();
  const [selectedSource, setSelectedSource] = useState('');
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [schedule, setSchedule] = useState({
    field: 'hours',
    value: 6
  });
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [workflowId, setWorkflowId] = useState(null);
  const [step, setStep] = useState(1); // 1: Select Source, 2: Credentials, 3: Schedule, 4: Test & Create

  const handleSourceSelect = (sourceId) => {
    setSelectedSource(sourceId);
    setStep(2);
    setTestResult(null);
  };

  const handleCredentialChange = (field, value) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTestConnection = async () => {
    if (!selectedSource || !credentials.username || !credentials.password) {
      alert('Please fill in all credential fields');
      return;
    }

    setIsLoading(true);
    setTestResult(null);

    try {
      const result = await testConnection(selectedSource, {
        username: credentials.username,
        password: credentials.password
      });

      setTestResult(result);
      
      if (result.success) {
        setTimeout(() => setStep(4), 1500);
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || 'Connection test failed'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateWorkflow = async () => {
    if (!currentEntity) {
      alert('Please select an entity first');
      return;
    }

    setIsLoading(true);

    try {
      // 1. Create secure credentials in AWS Secrets Manager
      const credentialSecretName = await createSecureCredentials(
        selectedSource,
        currentEntity.id,
        credentials
      );

      // 2. Create workflow
      const workflowId = await createDataIngestionWorkflow(
        selectedSource,
        credentialSecretName,
        currentEntity.id,
        schedule
      );

      setWorkflowId(workflowId);
      
      // Show success message
      alert('Workflow created successfully! You can now activate it from the N8N Monitor page.');
      
      // Reset form
      resetForm();
      
    } catch (error) {
      console.error('Error creating workflow:', error);
      alert(`Failed to create workflow: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedSource('');
    setCredentials({ username: '', password: '' });
    setSchedule({ field: 'hours', value: 6 });
    setStep(1);
    setTestResult(null);
    setWorkflowId(null);
  };

  const dataSourceList = Object.values(DATA_SOURCES);

  return (
    <div className="data-source-setup">
      <div className="setup-header">
        <h2>🔗 Data Source Setup</h2>
        <p>Connect financial portals to automatically sync your data</p>
        {currentEntity && (
          <div className="current-entity">
            <span>Setting up for: <strong>{currentEntity.name}</strong></span>
          </div>
        )}
      </div>

      <div className="setup-steps">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <span className="step-number">1</span>
          <span className="step-label">Select Source</span>
        </div>
        <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <span className="step-number">2</span>
          <span className="step-label">Credentials</span>
        </div>
        <div className={`step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
          <span className="step-number">3</span>
          <span className="step-label">Schedule</span>
        </div>
        <div className={`step ${step >= 4 ? 'active' : ''} ${step > 4 ? 'completed' : ''}`}>
          <span className="step-number">4</span>
          <span className="step-label">Test & Create</span>
        </div>
      </div>

      <div className="setup-content">
        {step === 1 && (
          <div className="step-content">
            <h3>Choose Your Financial Data Source</h3>
            <p>Select the portal where you want to fetch financial data from:</p>
            
            <div className="data-sources-grid">
              {dataSourceList.map((source) => (
                <div 
                  key={source.id}
                  className={`data-source-card ${selectedSource === source.id ? 'selected' : ''}`}
                  onClick={() => handleSourceSelect(source.id)}
                >
                  <div className="source-icon">
                    {source.type === 'bank' ? '🏦' : '📊'}
                  </div>
                  <div className="source-info">
                    <h4>{source.name}</h4>
                    <p className="source-type">{source.type}</p>
                    <p className="source-url">{source.url}</p>
                  </div>
                  <div className="source-auth">
                    <span className="auth-type">
                      {source.credentialType === 'httpBasicAuth' ? '🔐 Username/Password' : '🔑 API Key'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="step-content">
            <h3>🔐 Enter Your Credentials</h3>
            <p>Your credentials will be stored securely in N8N's encrypted credential store.</p>
            
            <div className="selected-source-info">
              <div className="source-icon">
                {DATA_SOURCES[selectedSource.toUpperCase()].type === 'bank' ? '🏦' : '📊'}
              </div>
              <div>
                <h4>{DATA_SOURCES[selectedSource.toUpperCase()].name}</h4>
                <p>{DATA_SOURCES[selectedSource.toUpperCase()].url}</p>
              </div>
            </div>

            <form className="credentials-form">
              <div className="form-group">
                <label htmlFor="username">Username / Email</label>
                <input
                  type="text"
                  id="username"
                  value={credentials.username}
                  onChange={(e) => handleCredentialChange('username', e.target.value)}
                  placeholder="Enter your username or email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={credentials.password}
                  onChange={(e) => handleCredentialChange('password', e.target.value)}
                  placeholder="Enter your password"
                />
              </div>

              <div className="security-notice">
                <span className="security-icon">🔒</span>
                <div>
                  <strong>Security Notice</strong>
                  <p>Credentials are encrypted and stored securely in N8N. They are never stored in your browser or our database.</p>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setStep(1)}
                >
                  ← Back
                </button>
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={() => setStep(3)}
                  disabled={!credentials.username || !credentials.password}
                >
                  Next: Schedule →
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="step-content">
            <h3>⏰ Set Data Sync Schedule</h3>
            <p>How often should we fetch new data from this source?</p>

            <div className="schedule-form">
              <div className="schedule-options">
                <div 
                  className={`schedule-option ${schedule.field === 'hours' && schedule.value === 1 ? 'selected' : ''}`}
                  onClick={() => setSchedule({ field: 'hours', value: 1 })}
                >
                  <span className="schedule-icon">⚡</span>
                  <div>
                    <strong>Every Hour</strong>
                    <p>Real-time updates (high resource usage)</p>
                  </div>
                </div>

                <div 
                  className={`schedule-option ${schedule.field === 'hours' && schedule.value === 6 ? 'selected' : ''}`}
                  onClick={() => setSchedule({ field: 'hours', value: 6 })}
                >
                  <span className="schedule-icon">🔄</span>
                  <div>
                    <strong>Every 6 Hours</strong>
                    <p>Regular updates (recommended)</p>
                  </div>
                </div>

                <div 
                  className={`schedule-option ${schedule.field === 'days' && schedule.value === 1 ? 'selected' : ''}`}
                  onClick={() => setSchedule({ field: 'days', value: 1 })}
                >
                  <span className="schedule-icon">📅</span>
                  <div>
                    <strong>Daily</strong>
                    <p>Once per day (efficient)</p>
                  </div>
                </div>

                <div 
                  className={`schedule-option ${schedule.field === 'days' && schedule.value === 7 ? 'selected' : ''}`}
                  onClick={() => setSchedule({ field: 'days', value: 7 })}
                >
                  <span className="schedule-icon">📆</span>
                  <div>
                    <strong>Weekly</strong>
                    <p>Once per week (minimal)</p>
                  </div>
                </div>
              </div>

              <div className="schedule-summary">
                <span className="summary-icon">ℹ️</span>
                <div>
                  <strong>Current Setting:</strong>
                  <p>Data will be fetched every {schedule.value} {schedule.field}</p>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setStep(2)}
                >
                  ← Back
                </button>
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={handleTestConnection}
                  disabled={isLoading}
                >
                  {isLoading ? 'Testing...' : 'Test Connection →'}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="step-content">
            <h3>🧪 Test Connection & Create Workflow</h3>
            
            {testResult && (
              <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                <span className="result-icon">
                  {testResult.success ? '✅' : '❌'}
                </span>
                <div>
                  <strong>{testResult.success ? 'Connection Successful!' : 'Connection Failed'}</strong>
                  <p>{testResult.message}</p>
                </div>
              </div>
            )}

            {testResult?.success && (
              <div className="workflow-creation">
                <h4>Create N8N Workflow</h4>
                <p>Your connection test was successful. Click below to create the automated workflow:</p>
                
                <div className="workflow-summary">
                  <div className="summary-row">
                    <span className="label">Data Source:</span>
                    <span className="value">{DATA_SOURCES[selectedSource.toUpperCase()].name}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Entity:</span>
                    <span className="value">{currentEntity?.name}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Schedule:</span>
                    <span className="value">Every {schedule.value} {schedule.field}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Authentication:</span>
                    <span className="value">Secure credential store</span>
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => setStep(3)}
                  >
                    ← Modify Settings
                  </button>
                  <button 
                    type="button" 
                    className="btn-primary"
                    onClick={handleCreateWorkflow}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating Workflow...' : '🚀 Create Workflow'}
                  </button>
                </div>
              </div>
            )}

            {!testResult?.success && testResult && (
              <div className="retry-section">
                <p>Please check your credentials and try again.</p>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => setStep(2)}
                  >
                    ← Update Credentials
                  </button>
                  <button 
                    type="button" 
                    className="btn-primary"
                    onClick={handleTestConnection}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Testing...' : 'Retry Test'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {workflowId && (
        <div className="success-message">
          <span className="success-icon">🎉</span>
          <div>
            <strong>Workflow Created Successfully!</strong>
            <p>Workflow ID: {workflowId}</p>
            <p>You can monitor and activate this workflow from the N8N Monitor page.</p>
          </div>
          <button 
            className="btn-primary"
            onClick={resetForm}
          >
            Create Another Workflow
          </button>
        </div>
      )}
    </div>
  );
}

export default DataSourceSetup; 